import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Divider, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from "@mui/material";
import { useTypedDispatch, useTypedSelector } from "../../../../redux/hooks"
import { QueueAgentStats, selectQueuePanelUserData } from "../../../../redux/slices/queueSlice"
import { selectAuthUserByUuid, selectUserStatus, setViewContact } from "../../../../redux/slices";
import styles from '../calls_queue.module.scss';
import EntityIcon from "../../../../components/icons/EntityIcon";
import { visuallyHidden } from "../../../../helpers";
import { statusCheckerColor } from "../../../../components/icons/EntityIcon/UserEntityIcon/children/BlfIcon";
import { IUserStatus } from "../../../../types";
import { numSecToString } from "..";
import { usePanelContext } from "../panelContext";

const AgentStatusLine: React.FC<{ status: IUserStatus | undefined; specificQueueLogin: boolean }> = ({ status, specificQueueLogin }) => {
    if (!status) {
        return null;
    };

    const statusClr = statusCheckerColor(status);

    let clr: string | undefined;
    let data: string | undefined;

    switch (statusClr) {
        case 'in_call':
            clr = '--color-danger';
            data = 'On Call';
            break;
        case 'ringing':
            clr = '--notification-color';
            data = 'Ringing';
            break;
        case 'offline':
            clr = '--secondary-font-color';
            data = 'Offline';
            break;
        case 'aux':
            clr = '--color-warning';
            data = status.aux_code?.code || 'Paused';
            break;
        case 'available':
            clr = '--color-success';
            data = 'Available';
            break;
        case 'dnd':
            clr = '--color-danger';
            data = 'Do Not Disturb';
            break;
    }

    if (!status.queue_login) {
        clr = '--secondary-font-color';
        data = 'Logged Off';
    } else if (!specificQueueLogin) {
        clr = '--secondary-font-color';
        data = 'Logged Off (Queue)';
    }

    return (
        <span className={styles.label} style={{ color: `var(${clr})` }}>
            {data}
        </span>
    );
};

const LastCallTime = ({ lastTime }: { lastTime: string | null }) => {
    const spanRef = useRef<HTMLSpanElement>(null);
    const startSecondsRef = useRef<number | null>(null);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const updateDisplay = useCallback(() => {
        if (!spanRef.current) return;

        if (startSecondsRef.current === null) {
            spanRef.current.textContent = '-';
            return;
        }

        spanRef.current.textContent = numSecToString(startSecondsRef.current);

        startSecondsRef.current += 1;
    }, []);

    useEffect(() => {
        let newSeconds: number | null = null;
        if (lastTime) {
            const callDate = new Date(lastTime);
            if (!Number.isNaN(callDate.getTime())) {
                const diffSeconds = Math.floor((Date.now() - callDate.getTime()) / 1000);
                newSeconds = Math.max(diffSeconds, 0);
            }
        }

        const oldSeconds = startSecondsRef.current;

        startSecondsRef.current = newSeconds;
        updateDisplay();

        if (newSeconds === null) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = null;
        } else if (!intervalRef.current || oldSeconds === newSeconds) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = setInterval(updateDisplay, 1000);
        }

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = null;
        };
    }, [lastTime]);

    return <span ref={spanRef}>-</span>;
}

const AgentRow: React.FC<{
    data: QueueAgentStats,
    showLasCallTime: boolean,
}> = ({
    data,
    showLasCallTime
}) => {
        const { includeOffline } = usePanelContext();

        const status = useTypedSelector(state => selectUserStatus(state, data.name));
        const user = useTypedSelector(state => selectAuthUserByUuid(state, data.uuid));

        const dispatch = useTypedDispatch();

        if ((!includeOffline && (!status || statusCheckerColor(status) === 'offline')) || !user) {
            return null;
        }

        return (
            <TableRow>
                <TableCell className={styles.tableCell}>
                    <Box display="flex" alignItems="center">
                        <EntityIcon
                            type="user"
                            details={{
                                display_name: data.display_name || "",
                                avatar_key: data.avatar_key,
                                uuid: data.uuid,
                            }}
                            onClick={() => dispatch(setViewContact(user))}
                            size={28}
                        />
                        <Box>
                            <div>
                                <p>{user.nickname}</p>
                                <AgentStatusLine status={status} specificQueueLogin={data.specific_queue_login} />
                            </div>
                        </Box>
                    </Box>
                </TableCell>
                <TableCell className={styles.tableCell}>{data.num_calls}</TableCell>
                <TableCell className={styles.tableCell}>
                    {numSecToString(data.talk_time)}
                </TableCell>
                <TableCell className={styles.tableCell}>{data.total_failed_times}</TableCell>
                <TableCell className={styles.tableCell}>
                    {numSecToString(data.average_ring_duration)}
                </TableCell>
                {showLasCallTime && (
                    <TableCell className={styles.tableCell}>
                        <LastCallTime lastTime={data.last_call_end} />
                    </TableCell>
                )}
            </TableRow>
        );
    };

const AgentTable: React.FC<{
    queueUuid: string[] | string | undefined;
    dataLoading: boolean;
}> = ({ queueUuid, dataLoading }) => {

    const userStats = useTypedSelector(state => selectQueuePanelUserData(state, queueUuid));

    const [order, setOrder] = useState<number>(1);
    const [orderBy, setOrderBy] = useState<keyof QueueAgentStats>('display_name');

    const isSingleQueue = typeof queueUuid === 'string';

    const sortedData = useMemo(() =>
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        userStats.toSorted((a, b) => {
            switch (orderBy) {
                case "display_name":
                    return a.display_name.localeCompare(b.display_name) * order;
                case "num_calls":
                    return (a.num_calls > b.num_calls ? 1 : -1) * order;
                case "talk_time":
                    return (a.talk_time > b.talk_time ? 1 : -1) * order;
                case "total_failed_times":
                    return (a.total_failed_times > b.total_failed_times ? 1 : -1) * order;
                case "average_ring_duration":
                    return (a.average_ring_duration > b.average_ring_duration ? 1 : -1) * order;
                case "last_call_end":
                    if (!a.last_call_end && !b.last_call_end) return 0;

                    if (!a.last_call_end) return 1;

                    if (!b.last_call_end) return -1;

                    return a.last_call_end.localeCompare(b.last_call_end) * order
                default:
                    return 0;
            }
        })
        , [order, orderBy, userStats, isSingleQueue]);

    const headCells: {
        id: keyof QueueAgentStats,
        label: string,
        sortable: boolean
    }[] = useMemo(() => {
        const baseHeadCells: {
            id: keyof QueueAgentStats,
            label: string,
            sortable: boolean
        }[] = [
                { id: 'display_name', label: 'Agent', sortable: true },
                { id: 'num_calls', label: 'Answered Calls', sortable: true },
                { id: 'talk_time', label: 'Talk Time', sortable: true },
                { id: 'total_failed_times', label: 'Missed Calls', sortable: true },
                { id: 'average_ring_duration', label: 'Average Ring Time', sortable: true },
            ];

        if (isSingleQueue) {
            baseHeadCells.push({ id: 'last_call_end', label: 'Time Since Last Call', sortable: true });
        }

        return baseHeadCells;
    }, [])

    return (
        <TableContainer component={Paper} className={styles.agentTableContainer}>
            <Table aria-label="Agent Table" size="small" className={styles.agentTable}>
                <TableHead>
                    <TableRow>
                        {headCells.map((headCell) => {
                            if (!headCell.sortable) {
                                return (
                                    <TableCell key={String(headCell.id)} className={styles.tableHeadCell}>
                                        {headCell.label}
                                    </TableCell>
                                );
                            }
                            const isActive = headCell.id === orderBy;
                            return (
                                <TableCell key={String(headCell.id)}
                                    className={`${styles.tableHeadCell} ${styles.responsiveHeader}`}>
                                    <TableSortLabel
                                        active={isActive}
                                        direction={(isActive && order === -1) ? 'asc' : 'desc'}
                                        onClick={() => {
                                            if (isActive) {
                                                setOrder(order * -1);
                                            } else {
                                                setOrderBy(headCell.id)
                                                setOrder(1);
                                            }
                                        }}
                                        sx={{
                                            '& .MuiTableSortLabel-icon': {
                                                color: 'var(--secondary-font-color) !important',
                                            },
                                            '&.Mui-active .MuiTableSortLabel-icon': {
                                                color: 'var(--primary-font-color) !important',
                                            },
                                        }}
                                    >
                                        <span className={styles.truncatedText}>{headCell.label}</span>
                                        {isActive && (
                                            <Box component="span" sx={visuallyHidden}>
                                                {order === 1 ? 'sorted descending' : 'sorted ascending'}
                                            </Box>
                                        )}
                                    </TableSortLabel>
                                </TableCell>
                            );
                        })}
                    </TableRow>
                </TableHead>
                <TableRow>
                    <TableCell colSpan={headCells.length}>
                        <Divider />
                    </TableCell>
                </TableRow>
                <TableBody>
                    {dataLoading
                        ? (
                            <TableRow >
                                {Array.from({ length: headCells.length }).map((__, cellIndex) => (
                                    <TableCell key={cellIndex}>
                                        <Skeleton variant="text" height={20} />
                                    </TableCell>
                                ))}
                            </TableRow>
                        )
                        : sortedData.map(data => (
                            <AgentRow
                                key={(isSingleQueue ? queueUuid : '') + data.uuid}
                                data={data}
                                showLasCallTime={isSingleQueue}
                            />
                        ))
                    }
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default AgentTable;
